import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, useAuth } from './AuthContext';

const TRADES_TUTORIAL_KEY = 'trades_tutorial_done';

export function shouldShowTradesTutorial(): boolean {
  return !localStorage.getItem(TRADES_TUTORIAL_KEY);
}

interface TradeTutorialProps {
  onComplete: () => void;
  isMobile: boolean;
  onSetTab: (tab: 'incoming' | 'my' | 'ongoing' | 'history') => void;
  onRefresh: () => void;
}

interface SpotlightRect { top: number; left: number; width: number; height: number; }

function getRect(selector: string): SpotlightRect | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

const PAD = 10;
const TOOLTIP_H_EST = 260;


export default function TradeTutorial({ onComplete, isMobile, onSetTab, onRefresh }: TradeTutorialProps) {
  const { user } = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const [visible, setVisible] = useState(false);
  const [tradeId, setTradeId] = useState<string | null>(null);
  const tradeIdRef = useRef<string | null>(null);
  const measureTimers = useRef<number[]>([]);

  const dbUpdate = useCallback(async (patch: Record<string, any>) => {
    if (!tradeIdRef.current) return;
    await supabase.from('trade_requests').update(patch).eq('id', tradeIdRef.current);
    onRefresh();
  }, [onRefresh]);

  const dbDelete = useCallback(async () => {
    if (!tradeIdRef.current) return;
    await supabase.from('trade_requests').delete().eq('id', tradeIdRef.current);
    tradeIdRef.current = null;
    setTradeId(null);
    onRefresh();
  }, [onRefresh]);

  interface Step {
    id: string;
    title: string;
    body: string;
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
    target?: string;
    actionLabel?: string;
    remeasureDelay?: number;
    onEnter?: () => void;
    waitForAction?: boolean;
  }

  const steps: Step[] = [
    {
      id: 'welcome',
      placement: 'center',
      title: '⇄ Welcome to Trades',
      body: "This is where all your Animal Crossing trades live. Let's walk through the full trade flow together — we'll use a live trade for Ace to show you how it works.",
      actionLabel: "Let's go →",
    },
    {
      id: 'tabs',
      target: '.tr-tabs',
      placement: 'bottom',
      title: '📑 The Four Tabs',
      body: "Incoming — requests/offers from others.\nOutgoing — trades you sent.\nOngoing — active trades in progress.\nHistory — completed trades for 28 days.",
    },
    {
      id: 'incoming-subtabs',
      target: '.tr-subtabs',
      placement: 'bottom',
      title: '📥 Incoming Sub-Tabs',
      body: "Incoming splits into three:\n📥 Requests — standard shop requests\n🎁 Offers — someone offering you a wishlist villager\n↩️ Counters — counter-offers",
      onEnter: () => onSetTab('incoming'),
    },
    {
      id: 'outgoing-tab',
      target: '.tr-tabs .tr-tab:nth-child(2)',
      placement: 'bottom',
      title: '📤 Outgoing Tab',
      body: "Outgoing shows requests you've sent. Expand a card to see status or cancel via the Danger Zone.",
    },
    {
      id: 'ongoing-intro',
      target: '.tr-tabs .tr-tab:nth-child(3)',
      placement: 'bottom',
      title: '🔄 Ongoing Tab',
      body: "Ongoing trades are accepted trades in progress. Both parties work through 3 steps together. Here's your trade for Ace with User #3 — let's walk through it.",
      remeasureDelay: 100,
      onEnter: async () => {
        if (!user) return;
        const { data: u3 } = await supabase
          .from('ac_users').select('id').eq('user_number', 3).maybeSingle();
        const acceptorId = u3?.id || user.id;
        const { data: row } = await supabase.from('trade_requests').insert({
          requester_id: user.id,
          acceptor_id: acceptorId,
          villager_name: 'Ace',
          offer_text: '500 Bells',
          status: 'ongoing',
          trade_step: 1,
          plot_available: false,
          dodo_code: null,
          trade_type: 'request',
        }).select('id').single();
        if (row?.id) {
          tradeIdRef.current = row.id;
          setTradeId(row.id);
          onSetTab('ongoing');
          onRefresh();
        }
      },
    },
    {
      id: 'ongoing-card',
      target: '.tr-ongoing-card',
      placement: 'right',
      title: '🐦 Your Trade with #3',
      body: "Here's your ongoing trade for Ace. You're the Tradee — you're receiving the villager. User #3 is the Trader — they own Ace and are sending it to you.",
      remeasureDelay: 800,
      onEnter: () => { onSetTab('ongoing'); onRefresh(); },
    },
    {
      id: 'progress-bar',
      target: '.tr-progress-bar-wrap',
      placement: 'bottom',
      title: '📊 Trade Progress',
      body: "Every trade has 3 steps:\n1️⃣ Accepted — trade is live\n2️⃣ Plot & Dodo — you confirm a plot, trader opens gates\n3️⃣ On Island — fly over and collect the villager",
      remeasureDelay: 200,
    },
    {
      id: 'trader-boxed',
      target: '.tr-ongoing-card .tr-ongoing-body',
      placement: 'top',
      title: '📦 Trader Boxes Ace',
      body: "Step 1 is the Trader's turn — they box their villager. User #3 has just boxed Ace, moving to Step 2. Now it's your turn!",
      remeasureDelay: 300,
      onEnter: () => dbUpdate({ trade_step: 2 }),
    },
    {
      id: 'plot-btn',
      target: '.tr-ongoing-card .tr-step-btn.confirm',
      placement: 'top',
      title: '🏝️ Confirm Your Plot',
      body: "Press \"I have an available plot\" on the card above — the tutorial will move on automatically once you do.",
      remeasureDelay: 600,
      waitForAction: true,
    },
    {
      id: 'dodo-reveal',
      target: '.tr-ongoing-card .tr-dodo-reveal',
      placement: 'top',
      title: '✈️ Dodo Code Revealed',
      body: "The Dodo code is now visible — only you can see it. Fly to the trader's island, find Ace, and invite them to move in!",
      remeasureDelay: 800,
      onEnter: () => dbUpdate({ plot_available: true, dodo_code: 'EX4MP', trade_step: 3 }),
    },
    {
      id: 'mark-complete',
      target: '.tr-ongoing-card .tr-ongoing-body',
      placement: 'top',
      title: '✓ Mark Complete',
      body: "Once you've picked up Ace, the Trader marks the trade complete. After 24h you can do it yourself too. The trade then moves to History.",
      remeasureDelay: 400,
    },
    {
      id: 'history-tab',
      target: '.tr-tabs .tr-tab:nth-child(4)',
      placement: 'bottom',
      title: '📜 History Tab',
      body: "Once a trade is marked complete it moves here and stays for 28 days. You can see who you traded with and flag any issues.",
      remeasureDelay: 400,
      onEnter: () => dbUpdate({ status: 'completed', trade_step: 4, completed_at: new Date().toISOString() }),
    },
    {
      id: 'history-card',
      target: '.tr-history-card',
      placement: 'right',
      title: '📜 Completed Trade',
      body: "Your completed Ace trade lives here. You can see who you traded with, when it happened, and how many days before it's cleared from history.",
      remeasureDelay: 800,
      onEnter: () => { onSetTab('history'); onRefresh(); },
    },
    {
      id: 'report-btn',
      target: '.tr-report-btn',
      placement: 'left',
      title: '⚠️ Reporting',
      body: "If something went wrong — trader didn't show, wrong villager, rude — file a report. Reports keep the community safe.",
      remeasureDelay: 300,
    },
    {
      id: 'done',
      placement: 'center',
      title: '🎉 You\'re all set!',
      body: "That's the full trade flow! Head to the Marketplace to find villagers and request your first trade. Happy trading! 🏝️",
      actionLabel: 'Start trading →',
      onEnter: () => dbDelete(),
    },
  ];

  const step = steps[stepIdx];

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, []);

  // Set body data attribute for current step
  useEffect(() => {
    document.body.setAttribute('data-trades-tutorial-step', step.id);
    return () => document.body.removeAttribute('data-trades-tutorial-step');
  }, [step.id]);


  const measureTarget = useCallback((targetOverride?: string) => {
    const target = targetOverride || step.target;
    if (!target) { setRect(null); return; }
    measureTimers.current.forEach(clearTimeout);
    measureTimers.current = [];
    let attempts = 0;
    const tryMeasure = () => {
      const el = document.querySelector(target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        const t = window.setTimeout(() => {
          const r = getRect(target);
          if (r) {
            setRect(r);
          } else if (attempts < 12) {
            attempts++;
            measureTimers.current.push(window.setTimeout(tryMeasure, 100));
          } else {
            setRect(null);
          }
        }, 120);
        measureTimers.current.push(t);
      } else if (attempts < 12) {
        attempts++;
        measureTimers.current.push(window.setTimeout(tryMeasure, 100));
      } else {
        setRect(null);
      }
    };
    tryMeasure();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setVisible(false);
    setRect(null);
    if (step.onEnter) step.onEnter();
    const delay = step.remeasureDelay ? step.remeasureDelay + 100 : 300;
    const t = window.setTimeout(() => {
      measureTarget();
      setVisible(true);
    }, delay);
    return () => clearTimeout(t);
  }, [stepIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = () => { if (step.target) measureTarget(); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [measureTarget, step.target]);

  // On the plot-btn step, poll DB every second to detect when user pressed the real button
  useEffect(() => {
    if (step.id !== 'plot-btn' || !tradeIdRef.current) return;
    const interval = window.setInterval(async () => {
      const { data } = await supabase
        .from('trade_requests').select('plot_available').eq('id', tradeIdRef.current!).single();
      if (data?.plot_available) {
        window.clearInterval(interval);
        setStepIdx(i => i + 1);
      }
    }, 1000);
    return () => window.clearInterval(interval);
  }, [step.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const advance = () => {
    if (stepIdx === steps.length - 1) {
      localStorage.setItem(TRADES_TUTORIAL_KEY, '1');
      onComplete();
    } else {
      setStepIdx(i => i + 1);
    }
  };

  const skip = async () => {
    await dbDelete();
    localStorage.setItem(TRADES_TUTORIAL_KEY, '1');
    onComplete();
  };

  // Tooltip positioning — same logic as main Tutorial
  const getTooltipStyle = (): React.CSSProperties => {
    if (!rect || step.placement === 'center') {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: isMobile ? 'min(92vw, 360px)' : '400px',
        width: isMobile ? 'min(92vw, 360px)' : '400px',
      };
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const TW = isMobile ? Math.min(vw * 0.9, 340) : 370;
    const placement = step.placement || 'bottom';
    const centreX = rect.left + rect.width / 2;
    const clampedLeft = Math.max(8, Math.min(centreX - TW / 2, vw - TW - 8));

    if (placement === 'bottom') {
      const top = rect.top + rect.height + PAD + 8;
      if (top + TOOLTIP_H_EST > vh - 8) {
        return { position: 'fixed', bottom: vh - rect.top + PAD + 8, left: clampedLeft, width: TW, maxWidth: TW };
      }
      return { position: 'fixed', top, left: clampedLeft, width: TW, maxWidth: TW };
    }
    if (placement === 'top') {
      const bottom = vh - rect.top + PAD + 8;
      return { position: 'fixed', bottom, left: clampedLeft, width: TW, maxWidth: TW };
    }
    if (placement === 'right') {
      const left = Math.min(rect.left + rect.width + PAD + 8, vw - TW - 8);
      const top = Math.max(8, Math.min(rect.top + rect.height / 2 - TOOLTIP_H_EST / 2, vh - TOOLTIP_H_EST - 8));
      return { position: 'fixed', top, left, width: TW, maxWidth: TW };
    }
    // left
    const left = Math.max(8, rect.left - TW - PAD - 8);
    const top = Math.max(8, Math.min(rect.top + rect.height / 2 - TOOLTIP_H_EST / 2, vh - TOOLTIP_H_EST - 8));
    return { position: 'fixed', top, left, width: TW, maxWidth: TW };
  };

  const spotlightStyle: React.CSSProperties = rect ? {
    position: 'fixed',
    top: rect.top - PAD,
    left: rect.left - PAD,
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
    borderRadius: 14,
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.76)',
    border: '1.5px solid rgba(255,140,30,0.7)',
    animation: 'tutorial-glow-pulse 1.8s ease-in-out infinite',
    pointerEvents: 'none',
    zIndex: 200001,
    transition: 'top 0.3s cubic-bezier(0.4,0,0.2,1), left 0.3s cubic-bezier(0.4,0,0.2,1), width 0.3s cubic-bezier(0.4,0,0.2,1), height 0.3s cubic-bezier(0.4,0,0.2,1)',
  } : {
    position: 'fixed',
    top: 0, left: 0, width: 0, height: 0,
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.76)',
    pointerEvents: 'none',
    zIndex: 200001,
  };

  const renderBlocker = () => {
    const blockEvt = (e: React.MouseEvent | React.TouchEvent) => { e.stopPropagation(); e.preventDefault(); };
    if (!step.waitForAction || !rect) {
      return (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 200000 }}
          onClick={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
          onTouchStart={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
          onTouchMove={e => e.preventDefault()}
        />
      );
    }
    const vw = window.innerWidth, vh = window.innerHeight;
    const ht = rect.top - PAD, hl = rect.left - PAD;
    const hb = rect.top + rect.height + PAD, hr = rect.left + rect.width + PAD;
    const s: React.CSSProperties = { position: 'fixed', zIndex: 200000, background: 'transparent' };
    return (
      <>
        <div style={{ ...s, top: 0, left: 0, width: vw, height: Math.max(0, ht) }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
        <div style={{ ...s, top: hb, left: 0, width: vw, height: Math.max(0, vh - hb) }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
        <div style={{ ...s, top: ht, left: 0, width: Math.max(0, hl), height: hb - ht }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
        <div style={{ ...s, top: ht, left: hr, width: Math.max(0, vw - hr), height: hb - ht }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
      </>
    );
  };

  return (
    <>
      <div
        className={`tutorial-root${visible ? ' visible' : ''}`}
        data-tutorial-step={step.id}
        style={{ position: 'fixed', inset: 0, zIndex: 200000, pointerEvents: step.waitForAction ? 'none' : 'all' }}
      >
        {renderBlocker()}

        {!rect && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.76)', zIndex: 200001, pointerEvents: 'none' }} />
        )}

        {rect && <div style={spotlightStyle} />}

        <div
          className="tutorial-card"
          style={{ ...getTooltipStyle(), pointerEvents: 'all', zIndex: 200002, transition: 'top 0.3s cubic-bezier(0.4,0,0.2,1), left 0.3s cubic-bezier(0.4,0,0.2,1), bottom 0.3s cubic-bezier(0.4,0,0.2,1)' }}
          onClick={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
          onTouchStart={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
        >
          <div className="tutorial-dots">
            {steps.map((_, i) => (
              <span key={i} className={`tutorial-dot ${i === stepIdx ? 'active' : i < stepIdx ? 'done' : ''}`} />
            ))}
          </div>

          <h3 className="tutorial-title">{step.title}</h3>
          <p className="tutorial-body" style={{ whiteSpace: 'pre-line' }}>{step.body}</p>

          <div className="tutorial-actions">
            <button className="tutorial-skip" onClick={skip}>Skip</button>
            {!step.waitForAction && stepIdx === steps.length - 1 && (
              <button className="tutorial-next" onClick={advance}>
                {step.actionLabel || 'Finish'}
              </button>
            )}
            {step.actionLabel && !step.waitForAction && stepIdx < steps.length - 1 && (
              <button className="tutorial-next" onClick={advance}>{step.actionLabel}</button>
            )}
            {!step.waitForAction && stepIdx < steps.length - 1 && (
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', alignSelf: 'center' }}>tap anywhere to continue</span>
            )}
          </div>

          <div className="tutorial-counter">{stepIdx + 1} / {steps.length}</div>
        </div>
      </div>
    </>
  );
}
